import { memo } from 'react';
import { Quote } from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { staggerDelay } from '@/lib/stagger';

const reviews = [
  {
    name: 'Laura M.',
    text: 'Marina es una profesional excepcional. Gracias a ella he conseguido cambiar mis hábitos alimenticios de forma sostenible. Su seguimiento por WhatsApp es increíble.',
    highlight: 'Perdí 12kg de forma saludable',
  },
  {
    name: 'Carlos R.',
    text: 'Como deportista, necesitaba un plan que se adaptara a mi entrenamiento. Marina entendió perfectamente mis necesidades y los resultados han sido espectaculares.',
    highlight: 'Rendimiento deportivo mejorado',
  },
  {
    name: 'Ana P.',
    text: 'Las recetas que me proporciona son deliciosas y muy fáciles de hacer. Por fin he encontrado una forma de comer sano sin aburrirme ni pasar hambre.',
    highlight: 'Recetas prácticas y deliciosas',
  },
  {
    name: 'Miguel S.',
    text: 'La atención personalizada de Marina es lo que marca la diferencia. Siempre está disponible para resolver dudas y motivarte cuando lo necesitas.',
    highlight: 'Atención 100% personalizada',
  },
];

const Reviews = memo(() => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="resenas" className="section-padding gradient-section">
      <div className="container-custom">
        {/* Section Header */}
        <div
          ref={ref}
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-primary font-medium mb-4">
            Testimonios
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Opiniones de{' '}
            <span className="text-gradient">pacientes satisfechos</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            La satisfacción de mis pacientes es mi mayor logro. 
            Descubre las experiencias de quienes ya han transformado su vida.
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {reviews.map((review, index) => (
            <ReviewCard key={index} review={review} index={index} />
          ))}
        </div>

      </div>
    </section>
  );
});

Reviews.displayName = 'Reviews';

interface ReviewCardProps {
  review: {
    name: string;
    text: string;
    highlight: string;
  };
  index: number;
}

const ReviewCard = memo(({ review, index }: ReviewCardProps) => {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <div
      ref={ref}
      className={`glass-card relative h-full flex flex-col transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      } ${staggerDelay(index)}`}
    >
      {/* Quote icon */}
      <Quote className="absolute top-6 right-6 w-10 h-10 text-rose-light" />

      {/* Highlight badge */}
      <span className="inline-flex px-3 py-1 bg-sage-light rounded-full text-sm font-medium text-sage-dark mb-4 w-fit">
        {review.highlight}
      </span>

      {/* Review text */}
      <p className="text-foreground mb-6 leading-relaxed flex-grow">
        "{review.text}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rose-light flex items-center justify-center">
          <span className="text-rose-dark font-medium">
            {review.name.charAt(0)}
          </span>
        </div>
        <span className="font-medium text-foreground">{review.name}</span>
      </div>
    </div>
  );
});

ReviewCard.displayName = 'ReviewCard';

export default Reviews;
